import { db } from './firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  collection, 
  getDocs, 
  query, 
  orderBy,
  onSnapshot
} from 'firebase/firestore';

// ==================== CACHE SYSTEM ====================
// Simple in-memory cache with timestamp
const cache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCached = (key) => {
  const item = cache[key];
  if (item && Date.now() - item.timestamp < CACHE_DURATION) {
    return item.data;
  }
  return null;
};

const setCache = (key, data) => {
  cache[key] = { data, timestamp: Date.now() };
};

const clearCache = (key) => {
  if (key) {
    delete cache[key];
  } else {
    // Clear all cache
    Object.keys(cache).forEach(k => delete cache[k]);
  }
};

// ==================== ORGANIZATION DATA ====================

// Get organization info (visi, misi, sejarah)
export const getOrganizationData = async () => {
  const cacheKey = 'organization';
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const docRef = doc(db, 'organization', 'data');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      setCache(cacheKey, data);
      return data;
    }
    
    // Return default data if no data exists yet
    const defaultData = {
      nama: "FKPO DIY",
      tagline: "Wadah Aspirasi Pelajar",
      description: "Forum Komunikasi Pengurus OSIS DIY hadir sebagai ruang kolaborasi untuk meningkatkan kualitas pelajar di DIY",
      visi: "Menjadikan FKPO DIY sebagai pionir dalam mewujudkan perubahan sosial yang progresif dan berkelanjutan melalui inovasi, kolaborasi, dan kepemimpinan yang inspiratif dan independen.",
      misi: [
        {
          title: "Meningkatkan Partisipasi",
          desc: "Mengajak seluruh anggota untuk aktif berpartisipasi dalam setiap kegiatan dan program organisasi."
        },
        {
          title: "Pemberdayaan SDM",
          desc: "Memberdayakan siswa supaya menjadi agen perubahan dengan memberikan pelatihan kepemimpinan."
        },
        {
          title: "Sustainabilitas",
          desc: "Menerapkan kaidah keberlanjutan dalam setiap aktivitas organisasi."
        },
        {
          title: "Transformasi Program",
          desc: "Melakukan transformasi dan keberlanjutan Program Kerja dengan inovasi baru."
        }
      ],
      sejarah: "FKPO DIY berdiri di bawah naungan Balai Pemuda dan Olahraga (BPO) Dinas Pendidikan Pemuda dan Olahraga DIY. Forum ini rutin memperbarui kepengurusannya setiap tahun sebagai wadah komunikasi dan koordinasi antar pelajar dari berbagai sekolah di DIY.",
      tentangLainnya: "FKPO (Forum Komunikasi Pengurus OSIS) DIY merupakan wadah aktif yang rutin memperbarui kepengurusannya setiap tahun di bawah naungan Balai Pemuda dan Olahraga (BPO) Dinas Pendidikan Pemuda dan Olahraga DIY.",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    return defaultData;
  } catch (error) {
    console.error("Error getting organization data:", error);
    return null;
  }
};

// Update organization data
export const updateOrganizationData = async (data) => {
  try {
    const docRef = doc(db, 'organization', 'data');
    await setDoc(docRef, {
      ...data,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    clearCache('organization');
    return { success: true };
  } catch (error) {
    console.error("Error updating organization data:", error);
    return { success: false, error: error.message };
  }
};

// ==================== PERIODS ====================

// Get all periods
export const getPeriods = async () => {
  const cacheKey = 'periods';
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const q = query(collection(db, 'periods'), orderBy('tahun', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const periods = [];
    querySnapshot.forEach((doc) => {
      periods.push({ id: doc.id, ...doc.data() });
    });
    
    // If no periods, return default
    if (periods.length === 0) {
      const defaultPeriods = [
        { 
          id: '2026-2027', 
          tahun: '2026-2027', 
          isActive: true,
          createdAt: new Date().toISOString()
        },
        { 
          id: '2025-2026', 
          tahun: '2025-2026', 
          isActive: false,
          createdAt: new Date().toISOString()
        }
      ];
      setCache(cacheKey, defaultPeriods);
      return defaultPeriods;
    }
    
    setCache(cacheKey, periods);
    return periods;
  } catch (error) {
    console.error("Error getting periods:", error);
    return [
      { id: '2026-2027', tahun: '2026-2027', isActive: true },
      { id: '2025-2026', tahun: '2025-2026', isActive: false }
    ];
  }
};

// Add new period
export const addPeriod = async (periodData) => {
  try {
    const docRef = doc(db, 'periods', periodData.tahun);
    await setDoc(docRef, {
      ...periodData,
      createdAt: new Date().toISOString()
    });
    
    clearCache('periods');
    return { success: true };
  } catch (error) {
    console.error("Error adding period:", error);
    return { success: false, error: error.message };
  }
};

// Update period
export const updatePeriod = async (periodId, data) => {
  try {
    const docRef = doc(db, 'periods', periodId);
    await updateDoc(docRef, data);
    
    clearCache('periods');
    clearCache(`period_${periodId}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating period:", error);
    return { success: false, error: error.message };
  }
};

// Delete period
export const deletePeriod = async (periodId) => {
  try {
    await deleteDoc(doc(db, 'periods', periodId));
    
    clearCache('periods');
    clearCache(`period_${periodId}`);
    return { success: true };
  } catch (error) {
    console.error("Error deleting period:", error);
    return { success: false, error: error.message };
  }
};

// Get specific period data (divisions, members)
export const getPeriodData = async (periodId) => {
  const cacheKey = `period_${periodId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const docRef = doc(db, 'periods', periodId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      setCache(cacheKey, data);
      return data;
    }
    
    return null;
  } catch (error) {
    console.error("Error getting period data:", error);
    return null;
  }
};

// Update period data (divisions, members)
export const updatePeriodData = async (periodId, data) => {
  try {
    const docRef = doc(db, 'periods', periodId);
    await setDoc(docRef, {
      ...data,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    clearCache('periods');
    clearCache(`period_${periodId}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating period data:", error);
    return { success: false, error: error.message };
  }
};

// ==================== PERIOD IMAGES ====================

// Get period images (background and home image)
export const getPeriodImages = async (periodId) => {
  const cacheKey = `periodImages_${periodId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const docRef = doc(db, 'periods', periodId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const images = {
        backgroundImage: data.backgroundImage || null,
        homeImage: data.heroImage || null
      };
      setCache(cacheKey, images);
      return images;
    }
    
    return { backgroundImage: null, homeImage: null };
  } catch (error) {
    console.error("Error getting period images:", error);
    return { backgroundImage: null, homeImage: null };
  }
};

// Update period images
export const updatePeriodImages = async (periodId, images) => {
  try {
    const docRef = doc(db, 'periods', periodId);
    await setDoc(docRef, {
      ...images,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    clearCache('periods');
    clearCache(`period_${periodId}`);
    clearCache(`periodImages_${periodId}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating period images:", error);
    return { success: false, error: error.message };
  }
};

// ==================== REAL-TIME LISTENERS ====================

// Listen to organization data changes
export const subscribeToOrganizationData = (callback) => {
  const docRef = doc(db, 'organization', 'data');
  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data());
    } else {
      callback(null);
    }
  });
};

// Listen to periods changes
export const subscribeToPeriods = (callback) => {
  const q = query(collection(db, 'periods'), orderBy('tahun', 'desc'));
  return onSnapshot(q, (querySnapshot) => {
    const periods = [];
    querySnapshot.forEach((doc) => {
      periods.push({ id: doc.id, ...doc.data() });
    });
    callback(periods);
  });
};

// ==================== DIVISIONS DATA ====================

// Default divisions structure
export const defaultDivisions = [
  {
    id: 'ketua',
    title: 'Ketua',
    icon: 'Crown',
    color: 'yellow',
    desc: 'Pemimpin utama yang bertanggung jawab atas jalannya kegiatan, arah kebijakan, serta pencapaian tujuan organisasi. Menuntut kemampuan kepemimpinan, komunikasi, dan pengambilan keputusan yang matang.',
    members: []
  },
  {
    id: 'sekretaris',
    title: 'Sekretaris',
    icon: 'FileText',
    color: 'blue',
    desc: 'Pengelola administrasi dan dokumentasi organisasi. Pusat informasi, pencatat keputusan, serta penghubung komunikasi antar anggota maupun dengan pihak luar.',
    members: []
  },
  {
    id: 'bendahara',
    title: 'Bendahara',
    icon: 'Wallet',
    color: 'emerald',
    desc: 'Bertanggung jawab atas pengelolaan keuangan organisasi. Menjaga transparansi, akuntabilitas, dan keberlanjutan pendanaan kegiatan organisasi.',
    members: []
  },
  {
    id: 'program_kerja',
    title: 'Divisi Program Kerja',
    icon: 'ClipboardCheck',
    color: 'indigo',
    desc: 'Bertanggung jawab atas perencanaan, pelaksanaan, dan evaluasi seluruh kegiatan. Motor penggerak yang memastikan visi dan misi terwujud melalui program nyata.',
    members: []
  },
  {
    id: 'sdm',
    title: 'Divisi SDM',
    icon: 'UserCog',
    color: 'pink',
    desc: 'Mengelola, mengembangkan, dan menjaga kualitas anggota. Memastikan setiap anggota memiliki kesempatan berkembang, merasa dihargai, dan berkontribusi optimal.',
    members: []
  },
  {
    id: 'humas',
    title: 'Divisi Humas',
    icon: 'Megaphone',
    color: 'orange',
    desc: 'Jembatan komunikasi dengan pihak eksternal maupun internal. Membangun citra positif, menjaga hubungan baik, dan memastikan informasi tersampaikan dengan jelas.',
    members: []
  },
  {
    id: 'dana_usaha',
    title: 'Divisi Dana Usaha',
    icon: 'Coins',
    color: 'teal',
    desc: 'Bertanggung jawab atas perencanaan dan pengembangan sumber pendanaan. Memastikan keberlangsungan program melalui pengelolaan dana yang transparan.',
    members: []
  },
  {
    id: 'multimedia',
    title: 'Divisi Multimedia',
    icon: 'Camera',
    color: 'purple',
    desc: 'Pengelola informasi visual, audio, dan digital. Wajah organisasi kepada publik sekaligus alat memperkuat identitas dan menyebarkan pesan.',
    members: []
  }
];

// Get divisions (from organization or default)
export const getDivisions = async () => {
  const orgData = await getOrganizationData();
  return orgData?.divisions || defaultDivisions;
};

// Update divisions
export const updateDivisions = async (divisions) => {
  return updateOrganizationData({ divisions });
};

// ==================== UTILITY ====================

// Force clear all cache (useful after updates)
export const clearAllCache = () => {
  clearCache();
};

// Initialize default data if not exists
export const initializeDefaultData = async () => {
  const orgData = await getOrganizationData();
  if (!orgData || !orgData.nama) {
    await updateOrganizationData({});
  }
  
  const periods = await getPeriods();
  if (periods.length === 0) {
    await addPeriod({ tahun: '2026-2027', isActive: true });
    await addPeriod({ tahun: '2025-2026', isActive: false });
  }
};

